import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { Menu, MenuCourse, MenuRecipeSummary } from '../types';
import { Section } from '../../../components/ui/Section';
import DataCompletenessChip from '../../RecipeV2/components/DataCompletenessChip';

interface MenuStructureTabProps {
  menu: Menu;
  recipeSummaries: MenuRecipeSummary[];
}

const CourseTable = ({
  courses, recipeSummaries, onDishClick,
}: { courses: MenuCourse[]; recipeSummaries: MenuRecipeSummary[]; onDishClick: (recipeId: number) => void }) => {
  const { t } = useTranslation('menus');
  return (
    <Stack spacing={4}>
      {courses.map((course) => (
        <Box key={course.name}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 2 }}>{course.name}</Typography>
          <Section style={{ boxShadow: 'none' }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('dish', 'Dish')}</TableCell>
                    <TableCell align="right">{t('price', 'Price')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {course.dishes.map((dish) => {
                    const summary = recipeSummaries.find((s) => s.recipeId === dish.recipeId);
                    return (
                      <TableRow
                        key={dish.recipeId}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => onDishClick(dish.recipeId)}
                      >
                        <TableCell>
                          <Stack direction="row"
                            spacing={1}
                            alignItems="center">
                            <Typography variant="textSM"
                              fontWeight={600}
                              color="primary.main">
                              {summary?.recipeName ?? t('unknownRecipe', 'Unknown recipe')}
                            </Typography>
                            <DataCompletenessChip completeness={summary?.dataCompleteness} />
                          </Stack>
                        </TableCell>
                        <TableCell align="right">£{dish.price.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Section>
        </Box>
      ))}
    </Stack>
  );
};

/** Course-by-course breakdown of dishes, linking each dish through to its RecipeV2 detail
 * page so the menu structure ties back to the underlying recipe catalogue. Menus with a
 * `dayStructure` (e.g. a weekly school lunch cycle) can toggle between the flat course view
 * and a nested Day > Course > Dish view; menus without one (hospitality, single-item offers)
 * only ever show the flat view. Dish name/data-completeness are resolved from `recipeSummaries`
 * (see useMenuDetail) rather than stored on the dish itself. */
const MenuStructureTab = ({ menu, recipeSummaries }: MenuStructureTabProps) => {
  const { t } = useTranslation('menus');
  const navigate = useNavigate();
  const hasDayStructure = !!menu.dayStructure?.length;
  const [mode, setMode] = useState<'flat' | 'byDay'>(hasDayStructure ? 'byDay' : 'flat');
  const onDishClick = (recipeId: number) => navigate(`/range/recipes/${recipeId}`);

  if (menu.courses.length === 0 && !hasDayStructure) {
    return (
      <Paper variant="outlined"
        sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">
          {t('noCourses', 'No courses have been added to this menu.')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack spacing={3}>
      {hasDayStructure && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={mode}
          onChange={(_e, value) => value && setMode(value)}
        >
          <ToggleButton value="byDay">{t('structureMode.byDay', 'By Day')}</ToggleButton>
          <ToggleButton value="flat">{t('structureMode.flat', 'Flat')}</ToggleButton>
        </ToggleButtonGroup>
      )}

      {mode === 'byDay' && hasDayStructure ? (
        <Stack spacing={5}>
          {menu.dayStructure!.map((dayEntry) => (
            <Box key={dayEntry.day}>
              <Typography variant="textLG"
                fontWeight={700}
                sx={{ mb: 2 }}>{dayEntry.day}</Typography>
              <CourseTable courses={dayEntry.courses}
                recipeSummaries={recipeSummaries}
                onDishClick={onDishClick} />
            </Box>
          ))}
        </Stack>
      ) : (
        <CourseTable courses={menu.courses}
          recipeSummaries={recipeSummaries}
          onDishClick={onDishClick} />
      )}
    </Stack>
  );
};

export default MenuStructureTab;
